import { Navigation } from "@/components/Navigation";
import { DailyDevotional } from "@/components/devotional/DailyDevotional";
import { DevotionalProgress } from "@/components/devotional/DevotionalProgress";
import { DevotionalNavigation } from "@/components/devotional/navigation/DevotionalNavigation";
import { useDevotionalNavigation } from "@/components/devotional/navigation/useDevotionalNavigation";
import { useDevotionalData } from "@/components/devotional/hooks/useDevotionalData";
import { useUserProgress } from "@/components/devotional/hooks/useUserProgress";
import { useSession } from "@supabase/auth-helpers-react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { CommunityTabs } from "@/components/community/CommunityTabs";
import { CommunityHeader } from "@/components/community/CommunityHeader";
import { useFeatureLimit } from "@/hooks/useFeatureLimit";
import { useTheme } from "@/components/ui/theme-provider";

export default function DailyDevotionals() {
  const session = useSession();
  const [searchParams] = useSearchParams();
  const dayParam = searchParams.get('day');
  const { features, incrementUsage } = useFeatureLimit();
  const { theme } = useTheme();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Only fetch user progress if authenticated
  const {
    completedDays,
    currentStreak,
    totalPoints,
    totalProgress,
    nextAvailableDay,
    refreshProgress,
    isLoading: progressLoading
  } = useUserProgress();

  const getInitialDay = () => {
    // If day is specified in URL, use that
    if (dayParam) {
      const paramDay = parseInt(dayParam, 10);
      if (!isNaN(paramDay) && paramDay >= 1 && paramDay <= 100) {
        return paramDay;
      }
    }
    
    // For authenticated users, use their progress
    if (session?.user?.id) {
      if (nextAvailableDay && nextAvailableDay <= 100) {
        return nextAvailableDay;
      }
      
      const storedDay = localStorage.getItem(`lastDevotionalDay_${session.user.id}`);
      if (storedDay) {
        const day = parseInt(storedDay, 10);
        if (completedDays.includes(day) || day === nextAvailableDay) {
          return day;
        }
      }
    }
    
    // Default to day 1 for non-authenticated users or fallback
    return 1;
  };

  const {
    currentDay,
    setCurrentDay,
    navigateDay,
    canAccessDay,
    isFirstDay,
    isLastDay
  } = useDevotionalNavigation({
    totalDays: 100,
    completedDays: session?.user?.id ? completedDays : [], // Empty array for non-authenticated users
    initialDay: getInitialDay(),
  });

  const {
    devotional,
    progress,
    showCompletion,
  } = useDevotionalData(currentDay, completedDays, refreshProgress);

  useEffect(() => {
    if (session?.user?.id && currentDay) {
      localStorage.setItem(`lastDevotionalDay_${session.user.id}`, currentDay.toString());
    }
  }, [currentDay, session?.user?.id]);

  const handleDevotionalComplete = async () => {
    if (!session?.user?.id) {
      const canUse = incrementUsage(features.DAILY_DEVOTIONAL.key);
      if (!canUse) return;
    }
    
    await refreshProgress();
  };

  useEffect(() => {
    // Only enforce access restrictions for authenticated users
    if (session?.user?.id && !progressLoading && nextAvailableDay && !canAccessDay(currentDay)) {
      setCurrentDay(nextAvailableDay);
    }
  }, [progressLoading, nextAvailableDay, canAccessDay, currentDay, setCurrentDay, session?.user?.id]);

  const getBackgroundClasses = () => {
    if (theme === "dark") {
      return "min-h-screen bg-gradient-to-b from-[#1A1A25] to-[#0F111A] text-white pb-32";
    }
    return "min-h-screen bg-app-background text-app-text-primary pb-32 transition-colors duration-300";
  };

  return (
    <div className={getBackgroundClasses()}>
      <div className="container py-6 max-w-4xl mx-auto px-4">
        <CommunityHeader />
        
        <section className="mb-6 md:mb-8 mt-6">
          <h1 className="text-2xl md:text-3xl font-bold text-center text-app-accent mb-2">
            Daily Devotionals
          </h1>
          <p className="text-sm md:text-base text-center text-app-text-secondary mb-4">
            Grow your faith daily through scripture and reflection
          </p>
          
          {/* Only show progress for authenticated users */}
          {session?.user?.id && (
            <DevotionalProgress
              totalProgress={totalProgress}
              currentStreak={currentStreak}
              totalPoints={totalPoints}
              daysCompleted={completedDays.length}
            />
          )}
        </section>

        {devotional ? (
          <>
            <DevotionalNavigation
              currentDay={currentDay}
              isCompleted={showCompletion}
              onNavigate={navigateDay}
              canAccessNextDay={session?.user?.id ? canAccessDay(currentDay + 1) : true}
              isFirstDay={isFirstDay}
              isLastDay={isLastDay}
            />

            <div className="bg-app-surface/10 backdrop-blur-sm rounded-lg p-4 md:p-6 shadow-lg mb-24 border border-app-border">
              <DailyDevotional 
                devotional={devotional} 
                progress={progress}
                onProgressUpdate={handleDevotionalComplete}
              />
            </div>
          </>
        ) : (
          <div className="text-center py-10">
            <p className="text-lg text-app-text-secondary">
              {progressLoading ? "Loading devotional content..." : "No devotional content available for this day."}
            </p>
          </div>
        )}
      </div>
      <CommunityTabs />
      <div className="relative">
        <Navigation />
      </div>
    </div>
  );
}
